import Graph from 'graphology';
import Sigma from 'sigma';
import forceAtlas2 from 'graphology-layout-forceatlas2';
import { ParticleField } from './particle-field.js';

const $ = (selector) => document.querySelector(selector);
const $$ = (selector) => [...document.querySelectorAll(selector)];

const state = {
  concepts: [],
  conceptIndex: new Map(),
  graphData: null,
  graph: null,
  renderer: null,
  selectedId: null,
  hoveredId: null,
  currentView: 'field',
  fieldGroups: [],
  prominentIds: [],
  cosmos: null,
  motionMode: localStorage.getItem('conceptual:motion') || 'auto',
  reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  layerMode: localStorage.getItem('conceptual:layer-mode') || 'field',
  hiddenLayers: {
    field: new Set(JSON.parse(localStorage.getItem('conceptual:hidden-layers:field') || '[]')),
    type: new Set(JSON.parse(localStorage.getItem('conceptual:hidden-layers:type') || '[]')),
    category: new Set(JSON.parse(localStorage.getItem('conceptual:hidden-layers:category') || '[]'))
  },
  ledgerEvents: [],
  ledgerSort: 'at',
  ledgerDirection: 'desc',
  ledgerPage: 1,
  ledgerPageSize: 20
};

const FIELD_COLOURS = {
  'Ecology': '#9bd46d',
  'Public life': '#efc76a',
  'Learning': '#8fc9ff',
  'Technology': '#bb9cff',
  'Health & care': '#ff9da8',
  'Culture': '#ffae73',
  'Mobility': '#72d8cf',
  'Housing': '#d9a2c7',
  'Food': '#d9dc7d',
  'Work': '#9fb2c7',
  'Open field': '#d8d8d8'
};

function colourFor(field) {
  if (FIELD_COLOURS[field]) return FIELD_COLOURS[field];
  let hash = 0;
  for (const char of field || '') hash = ((hash << 5) - hash) + char.charCodeAt(0);
  const palette = ['#96c7ff','#d2a3ff','#98ddb0','#ffc27d','#f4a0a7','#d7d47d','#7dd8d0'];
  return palette[Math.abs(hash) % palette.length];
}

const TYPE_COLOURS = {
  'Concept': '#9bc5ff',
  'Proposition': '#f3c96b',
  'Question': '#c6a5ff',
  'Observation': '#8fd6c9'
};

const LAYER_MODE_LABELS = {
  field: 'Field',
  type: 'Type',
  category: 'Category'
};

function titleCase(value = '') {
  return String(value).replace(/\b\w/g, (char) => char.toUpperCase());
}

function layerKeyForNode(node, mode = state.layerMode) {
  if (mode === 'type') return node.kind || 'Concept';
  if (mode === 'category') return (node.tags?.[0] || 'Uncategorised').trim();
  return node.field || 'Open field';
}

function layerColour(key, mode = state.layerMode) {
  if (mode === 'field') return colourFor(key);
  if (mode === 'type' && TYPE_COLOURS[key]) return TYPE_COLOURS[key];
  let hash = 0;
  for (const char of key || '') hash = ((hash << 5) - hash) + char.charCodeAt(0);
  const palette = ['#8fc9ff','#efc76a','#bb9cff','#72d8cf','#ff9da8','#ffae73','#9bd46d','#d9a2c7','#d9dc7d','#9fb2c7'];
  return palette[Math.abs(hash) % palette.length];
}

function conceptLayerKey(concept, mode = state.layerMode) {
  if (!concept) return 'Open field';
  if (mode === 'type') return concept.kind || 'Concept';
  if (mode === 'category') return (concept.tags?.[0] || 'Uncategorised').trim();
  return concept.field || 'Open field';
}

function colourForConcept(concept) {
  const key = conceptLayerKey(concept);
  return layerColour(key);
}

function hiddenLayersFor(mode = state.layerMode) {
  return state.hiddenLayers[mode] || new Set();
}

function saveHiddenLayers(mode = state.layerMode) {
  localStorage.setItem(`conceptual:hidden-layers:${mode}`, JSON.stringify([...hiddenLayersFor(mode)]));
}

function nodeIsVisible(node, mode = state.layerMode) {
  if (!node) return false;
  return !hiddenLayersFor(mode).has(layerKeyForNode(node, mode));
}

function conceptIsVisible(concept, mode = state.layerMode) {
  if (!concept) return false;
  return !hiddenLayersFor(mode).has(conceptLayerKey(concept, mode));
}

function fieldSeed(value = '') {
  let hash = 0;
  for (const char of value) hash = ((hash << 5) - hash) + char.charCodeAt(0);
  return Math.abs(hash) || 1;
}

function rgba(hex, alpha = 1) {
  const clean = String(hex || '').replace('#', '');
  const normal = clean.length === 3 ? clean.split('').map((x) => x + x).join('') : clean.padEnd(6, '0').slice(0, 6);
  const int = parseInt(normal, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function nodeViewport(id) {
  if (!state.renderer) return null;
  const display = state.renderer.getNodeDisplayData(id);
  if (!display) return null;
  const viewport = state.renderer.framedGraphToViewport({ x: display.x, y: display.y });
  if (!Number.isFinite(viewport.x) || !Number.isFinite(viewport.y)) return null;
  return { ...display, x: viewport.x, y: viewport.y };
}

function initialPosition(node, index, allNodes) {
  const fields = [...new Set(allNodes.map((n) => n.field))].sort();
  const fieldIndex = Math.max(0, fields.indexOf(node.field));
  const angle = (fieldIndex / Math.max(fields.length, 1)) * Math.PI * 2;
  let hash = 0;
  for (const char of node.id) hash = ((hash << 5) - hash) + char.charCodeAt(0);
  const jitterAngle = ((Math.abs(hash) % 360) / 180) * Math.PI;
  const clusterRadius = 12 + (Math.abs(hash >> 3) % 24);
  const orbit = 84;
  return {
    x: Math.cos(angle) * orbit + Math.cos(jitterAngle) * clusterRadius + (index % 3),
    y: Math.sin(angle) * orbit + Math.sin(jitterAngle) * clusterRadius
  };
}

async function api(path, options) {
  const response = await fetch(path, options);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || `Request failed: ${response.status}`);
  return data;
}

function formatDate(value, withTime = false) {
  const date = new Date(value);
  return new Intl.DateTimeFormat(undefined, withTime
    ? { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }
    : { day:'numeric', month:'short', year:'numeric' }).format(date);
}

function escapeHtml(value = '') {
  return String(value).replace(/[&<>'"]/g, (char) => ({ '&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;' }[char]));
}

function showToast(message, type = '') {
  const toast = $('#toast');
  toast.textContent = message;
  toast.className = `toast ${type}`.trim();
  toast.hidden = false;
  clearTimeout(showToast.timer);
  showToast.timer = setTimeout(() => { toast.hidden = true; }, 2600);
}

function buildLayerGroups(nodes, mode = state.layerMode) {
  const map = new Map();
  nodes.forEach((node) => {
    const key = layerKeyForNode(node, mode);
    if (!map.has(key)) {
      map.set(key, {
        key,
        label: mode === 'category' ? titleCase(key) : key,
        ids: [],
        seed: fieldSeed(`${mode}:${key}`),
        color: layerColour(key, mode)
      });
    }
    map.get(key).ids.push(node.id);
  });
  return [...map.values()].sort((a, b) => b.ids.length - a.ids.length || a.label.localeCompare(b.label));
}

async function loadApp() {
  const [concepts, graphData, status] = await Promise.all([
    api('/api/concepts'),
    api('/api/graph'),
    api('/api/status')
  ]);
  state.concepts = concepts;
  state.conceptIndex = new Map(concepts.map(concept => [concept.id, concept]));
  state.graphData = graphData;
  state.fieldGroups = buildLayerGroups(graphData.nodes);
  $('#registryStatus').textContent = `${status.concepts} concepts · open registry`;
  $('#fieldCount').textContent = `${status.concepts} registered concepts · ${graphData.edges.length} conceptual resonances`;
  renderLegend();
  renderGraph();
  routeFromHash();
}

function renderGraph() {
  state.renderer?.kill();
  const graph = new Graph({ type: 'undirected', multi: false, allowSelfLoops: false });
  const { nodes, edges } = state.graphData;

  nodes.forEach((node, index) => {
    const pos = initialPosition(node, index, nodes);
    graph.addNode(node.id, {
      label: node.title,
      x: pos.x,
      y: pos.y,
      size: 11,
      baseSize: 11,
      visualSize: 3.1,
      degree: 0,
      color: 'rgba(0,0,0,0)',
      field: node.field,
      kind: node.kind,
      tags: node.tags || [],
      title: node.title
    });
  });

  edges.forEach((edge) => {
    if (!graph.hasNode(edge.source) || !graph.hasNode(edge.target)) return;
    if (graph.hasEdge(edge.source, edge.target)) return;
    graph.addEdge(edge.source, edge.target, {
      size: edge.derived ? 1.1 : Math.max(.2, edge.score * 1.05),
      color: 'rgba(0,0,0,0)',
      relationScore: edge.score,
      derived: edge.derived
    });
  });

  nodes.forEach((node) => {
    const degree = graph.degree(node.id);
    const visualSize = clamp(2.8 + degree * 0.34, 2.8, 5.6);
    const hitSize = clamp(10 + degree * 0.28, 10, 13);
    graph.mergeNodeAttributes(node.id, { size: hitSize, baseSize: hitSize, visualSize, degree });
  });

  state.prominentIds = [...nodes]
    .sort((a, b) => graph.degree(b.id) - graph.degree(a.id))
    .slice(0, Math.min(24, nodes.length))
    .map((node) => node.id);

  if (graph.order > 1) {
    const settings = forceAtlas2.inferSettings(graph);
    forceAtlas2.assign(graph, {
      iterations: Math.min(230, 88 + graph.order * 3),
      settings: {
        ...settings,
        gravity: 0.13,
        scalingRatio: Math.max(4.8, Math.sqrt(graph.order) * 2.04),
        strongGravityMode: false,
        slowDown: 2.9
      }
    });
  }

  const renderer = new Sigma(graph, $('#graph'), {
    renderEdgeLabels: false,
    renderLabels: false,
    defaultDrawNodeLabel: () => {},
    defaultDrawNodeHover: () => {},
    labelRenderedSizeThreshold: 99999,
    labelDensity: 0,
    defaultEdgeColor: 'rgba(0,0,0,0)',
    minCameraRatio: .08,
    maxCameraRatio: 5,
    zIndex: true,
    nodeReducer: (node, data) => {
      const result = { ...data };
      const selected = state.selectedId === node;
      const hovered = state.hoveredId === node;
      result.hidden = !nodeIsVisible(data);
      result.label = '';
      result.color = 'rgba(0,0,0,0)';
      result.borderColor = 'rgba(0,0,0,0)';
      result.size = data.baseSize + (selected ? 2 : hovered ? 1 : 0);
      if (selected || hovered) result.zIndex = 6;
      return result;
    },
    edgeReducer: (edge, data) => ({ ...data, hidden: true, color: 'rgba(0,0,0,0)' })
  });

  renderer.on('enterNode', ({ node }) => {
    state.hoveredId = node;
    renderer.refresh();
    invalidateCosmos(true);
  });
  renderer.on('leaveNode', () => {
    state.hoveredId = null;
    renderer.refresh();
    invalidateCosmos(true);
  });
  renderer.on('clickNode', ({ node }) => openConcept(node, true));
  renderer.on('clickStage', () => { if (state.selectedId) closeConcept(); });
  renderer.getCamera().on('updated', () => renderer.refresh());
  renderer.on('afterRender', () => invalidateCosmos());

  state.graph = graph;
  state.renderer = renderer;
  ensureCosmos();
  state.cosmos.sceneDirty = true;
  startCosmos();
  renderer.refresh();
  applyDefaultFieldState();
}

function renderLegend() {
  const modes = ['field', 'type', 'category'];
  const groups = buildLayerGroups(state.graphData.nodes, state.layerMode);
  state.fieldGroups = groups;
  const hidden = hiddenLayersFor();
  const toggle = $('#legendToggle');
  toggle.innerHTML = `Layers · ${LAYER_MODE_LABELS[state.layerMode]} <span>⌄</span>`;

  $('#legend').innerHTML = `
    <div class="layer-panel-head">
      <div class="layer-panel-title-row">
        <span>Layer visibility</span>
        <button class="layer-show-all" id="layerShowAll"${hidden.size ? '' : ' disabled'}>Show all</button>
      </div>
      <div class="layer-mode-tabs">
        ${modes.map((mode) => `<button class="layer-mode-button${state.layerMode === mode ? ' is-active' : ''}" data-layer-mode="${mode}">${LAYER_MODE_LABELS[mode]}</button>`).join('')}
      </div>
    </div>
    <div class="layer-values">
      ${groups.slice(0, state.layerMode === 'category' ? 18 : 24).map((group) => {
        const isVisible = !hidden.has(group.key);
        return `<button class="legend-row layer-visibility-row${isVisible ? ' is-visible' : ' is-hidden'}" data-layer-key="${escapeHtml(group.key)}" aria-pressed="${isVisible}">
          <i class="legend-dot" style="background:${group.color};color:${group.color}"></i>
          <span>${escapeHtml(group.label)}</span>
          <em>${group.ids.length}</em>
          <i class="layer-visibility-switch" aria-hidden="true">${isVisible ? '✓' : ''}</i>
        </button>`;
      }).join('')}
    </div>`;

  $$('[data-layer-mode]').forEach((button) => button.addEventListener('click', () => {
    state.layerMode = button.dataset.layerMode;
    localStorage.setItem('conceptual:layer-mode', state.layerMode);
    state.fieldGroups = buildLayerGroups(state.graphData.nodes, state.layerMode);
    renderLegend();
    state.renderer?.refresh();
    invalidateCosmos(true);
  }));

  $$('[data-layer-key]').forEach((button) => button.addEventListener('click', () => {
    const key = button.dataset.layerKey;
    const set = hiddenLayersFor();
    if (set.has(key)) set.delete(key); else set.add(key);
    saveHiddenLayers();
    if (state.selectedId) {
      const selected = conceptById(state.selectedId);
      if (selected && !conceptIsVisible(selected)) closeConcept();
    }
    renderLegend();
    state.renderer?.refresh();
    invalidateCosmos(true);
  }));

  $('#layerShowAll')?.addEventListener('click', () => {
    hiddenLayersFor().clear();
    saveHiddenLayers();
    renderLegend();
    state.renderer?.refresh();
    invalidateCosmos(true);
  });
}

function conceptById(id) {
  return state.conceptIndex.get(id);
}

function ensureCosmos() {
  const fieldView = $('#fieldView');
  const starCanvas = $('#starCanvas');
  const nebulaCanvas = $('#nebulaCanvas');
  const labelCanvas = $('#labelCanvas');
  if (!fieldView || !starCanvas || !nebulaCanvas || !labelCanvas) return null;
  if (!state.cosmos) {
    state.cosmos = {
      fieldView,
      starCanvas,
      nebulaCanvas,
      labelCanvas,
      starCtx: starCanvas.getContext('2d'),
      particles: new ParticleField(nebulaCanvas),
      labelCtx: labelCanvas.getContext('2d'),
      stars: [],
      pointer: { x: 0, y: 0 },
      targetPointer: { x: 0, y: 0 },
      sceneDirty: true,
      time: 0,
      lastNow: 0,
      lastDraw: 0,
      raf: null,
      width: 0,
      height: 0,
      dpr: 1
    };
    window.addEventListener('resize', () => {
      resizeCosmos(true);
      requestAnimationFrame(() => {
        if (state.selectedId && !$('#conceptPanel').classList.contains('is-expanded')) frameSelectedConcept(state.selectedId, { keepZoom: true });
        invalidateCosmos(true);
      });
    });
    fieldView.addEventListener('pointermove', event => {
      if (event.pointerType !== 'mouse') return;
      state.cosmos.targetPointer = { x: (event.clientX / state.cosmos.width - .5) * 2, y: (event.clientY / state.cosmos.height - .5) * 2 };
    });
    fieldView.addEventListener('pointerleave', () => { state.cosmos.targetPointer = {x:0,y:0}; });
    nebulaCanvas.addEventListener('field-renderer-change', () => invalidateCosmos(true));
  }
  resizeCosmos(true);
  return state.cosmos;
}

function resizeCosmos(reseed = false) {
  const cosmos = state.cosmos;
  if (!cosmos) return;
  const width = cosmos.fieldView.clientWidth || window.innerWidth;
  const height = cosmos.fieldView.clientHeight || window.innerHeight;
  const dpr = Math.min(window.devicePixelRatio || 1, width < 720 ? 1.5 : 2);
  if (!reseed && width === cosmos.width && height === cosmos.height && dpr === cosmos.dpr) return;
  cosmos.width = width;
  cosmos.height = height;
  cosmos.dpr = dpr;

  [
    [cosmos.starCanvas, cosmos.starCtx],
    [cosmos.labelCanvas, cosmos.labelCtx]
  ].forEach(([canvas, ctx]) => {
    canvas.width = Math.max(1, Math.floor(width * dpr));
    canvas.height = Math.max(1, Math.floor(height * dpr));
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  });
  cosmos.particles.resize(width, height, dpr);
  cosmos.sceneDirty = true;

  if (reseed || !cosmos.stars.length) {
    let seed = 73621;
    const random = () => { seed = Math.imul(seed, 1664525) + 1013904223 | 0; return (seed >>> 0) / 4294967296; };
    const count = clamp(Math.round((width * height) / 2600), 170, 680);
    cosmos.stars = Array.from({ length: count }, () => ({
      x: random() * width,
      y: random() * height,
      size: .3 + Math.pow(random(), 3) * 1.35,
      alpha: .1 + random() * .44,
      depth: .25 + random() * 1.2,
      drift: .15 + random() * .7,
      twinkle: .8 + random() * 2.6,
      phase: random() * Math.PI * 2
    }));
  }
}

function motionEnabled() {
  return state.motionMode !== 'off' && (state.motionMode === 'on' || !state.reducedMotion);
}

function invalidateCosmos(scene = false) {
  if (!state.cosmos || $('#fieldView').hidden || document.hidden) return;
  if (scene) state.cosmos.sceneDirty = true;
  if (!state.cosmos.raf) state.cosmos.raf = requestAnimationFrame(drawCosmos);
}

function startCosmos() {
  ensureCosmos();
  if (state.cosmos) state.cosmos.lastNow = 0;
  invalidateCosmos();
}

function stopCosmos() {
  if (!state.cosmos?.raf) return;
  cancelAnimationFrame(state.cosmos.raf);
  state.cosmos.raf = null;
}

function boxesIntersect(a, b) {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

function roundRectPath(ctx, x, y, width, height, radius = 10) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}

function shortLabel(text, max = 42) {
  const value = String(text || '').trim();
  return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}

function placeLabelBox(width, height, anchorX, anchorY, boxWidth, boxHeight, preferLeft = false) {
  const topInset = 92;
  const sideInset = 22;
  const bottomInset = 42;
  let x = preferLeft ? anchorX - boxWidth - 16 : anchorX + 16;
  let y = anchorY - boxHeight / 2;
  if (x + boxWidth > width - sideInset) x = anchorX - boxWidth - 16;
  if (x < sideInset) x = sideInset;
  if (y < topInset) y = topInset;
  if (y + boxHeight > height - bottomInset) y = height - bottomInset - boxHeight;
  return { x, y };
}

function drawLabel(ctx, text, x, y, options = {}) {
  const fontSize = options.fontSize || 14;
  const fontWeight = options.fontWeight || 550;
  const mono = Boolean(options.mono);
  const paddingX = options.paddingX ?? 10;
  const paddingY = options.paddingY ?? 7;
  const bg = options.background ?? 'rgba(6,8,13,.66)';
  const border = options.border ?? 'rgba(255,255,255,.08)';
  const textColor = options.color ?? '#f6f8ff';
  const radius = options.radius ?? 13;

  ctx.font = `${fontWeight} ${fontSize}px ${mono ? '"Roboto Mono", ui-monospace, monospace' : '"Inter", ui-sans-serif, sans-serif'}`;
  const metrics = ctx.measureText(text);
  const width = Math.ceil(metrics.width + paddingX * 2);
  const height = Math.ceil(fontSize + paddingY * 2);
  const preferredX = options.boxX ?? x;
  const preferredY = options.boxY ?? (y - height / 2);
  const box = { x: preferredX, y: preferredY, width, height };
  const baselineY = box.y + height / 2;

  ctx.save();
  ctx.shadowBlur = 22;
  ctx.shadowColor = 'rgba(0,0,0,.34)';
  ctx.fillStyle = bg;
  roundRectPath(ctx, box.x, box.y, box.width, box.height, radius);
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.strokeStyle = border;
  ctx.lineWidth = 1;
  roundRectPath(ctx, box.x, box.y, box.width, box.height, radius);
  ctx.stroke();
  ctx.fillStyle = textColor;
  ctx.textBaseline = 'middle';
  ctx.fillText(text, box.x + paddingX, baselineY + .5);
  ctx.restore();
  return box;
}

function labelCandidates() {
  const ratio = state.renderer?.getCamera().getState().ratio ?? 1;
  const seen = new Set();
  const list = [];
  const add = (id, priority) => {
    if (!id || seen.has(id) || !state.graph?.hasNode(id)) return;
    const concept = conceptById(id);
    if (!concept || !conceptIsVisible(concept)) return;
    seen.add(id);
    list.push({ id, priority, concept });
  };

  if (state.selectedId) add(state.selectedId, 6);
  if (state.hoveredId && state.hoveredId !== state.selectedId) add(state.hoveredId, 5);

  const focus = state.selectedId || state.hoveredId;
  if (focus && state.graph?.hasNode(focus)) {
    const neighbours = state.graph.neighbors(focus).sort((a, b) => {
      const ea = state.graph.getEdgeAttribute(state.graph.edge(focus, a), 'relationScore') || 0;
      const eb = state.graph.getEdgeAttribute(state.graph.edge(focus, b), 'relationScore') || 0;
      return eb - ea;
    });
    neighbours.slice(0, state.selectedId ? 3 : 2).forEach(id => add(id, 3));
  }

  if (!state.selectedId && !state.hoveredId) {
    const prominentVisible = ratio < .22 ? 4 : ratio < .34 ? 2 : 0;
    state.prominentIds.slice(0, prominentVisible).forEach((id) => add(id, 2));
  }

  return list.filter((item) => item.concept);
}

function wrapFieldTitle(ctx, value, width, maxLines = 4) {
  const words = String(value).split(/\s+/);
  const lines = [];
  let line = '';
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width > width && line) { lines.push(line); line = word; }
    else line = next;
  }
  if (line) lines.push(line);
  if (lines.length > maxLines) lines.splice(maxLines - 1, lines.length, `${lines[maxLines - 1]}…`);
  return lines.map(line => {
    if (ctx.measureText(line).width <= width) return line;
    while (line.length > 1 && ctx.measureText(`${line}…`).width > width) line = line.slice(0, -1);
    return `${line}…`;
  });
}

function drawRelations(ctx, now) {
  const focus = state.selectedId || state.hoveredId;
  if (!focus || !state.graph.hasNode(focus)) return new Set();
  const active = new Set([focus, ...state.graph.neighbors(focus)]);
  const source = nodeViewport(focus);
  if (!source) return active;
  const colour = colourForConcept(conceptById(focus));
  ctx.save();
  state.graph.forEachEdge((edge, attrs, from, to) => {
    if (from !== focus && to !== focus) return;
    const targetId = from === focus ? to : from;
    if (!conceptIsVisible(conceptById(targetId))) return;
    const target = nodeViewport(targetId);
    if (!target) return;
    const dx = target.x - source.x, dy = target.y - source.y;
    const bend = ((fieldSeed(edge) % 3) - 1) * .09;
    const mid = { x: (source.x + target.x) / 2 - dy * bend, y: (source.y + target.y) / 2 + dx * bend };
    const gradient = ctx.createLinearGradient(source.x, source.y, target.x, target.y);
    gradient.addColorStop(0, rgba(colour, state.selectedId ? .47 : .35));
    gradient.addColorStop(1, rgba(colourForConcept(conceptById(targetId)), .17));
    ctx.strokeStyle = gradient;
    ctx.lineWidth = attrs.derived ? 1.4 : .7;
    ctx.beginPath();ctx.moveTo(source.x, source.y);ctx.quadraticCurveTo(mid.x, mid.y, target.x, target.y);ctx.stroke();
    const t = (now * .000026 + (fieldSeed(edge) % 100) / 100) % 1;
    const x = (1-t)**2 * source.x + 2*(1-t)*t*mid.x + t*t*target.x;
    const y = (1-t)**2 * source.y + 2*(1-t)*t*mid.y + t*t*target.y;
    ctx.fillStyle = rgba(colour, .55);
    ctx.beginPath();ctx.arc(x, y, 1.15, 0, Math.PI*2);ctx.fill();
  });
  ctx.restore();
  return active;
}

function drawOverlay(now) {
  const cosmos = state.cosmos;
  if (!cosmos || !state.renderer || !state.graph) return;
  const ctx = cosmos.labelCtx;
  ctx.clearRect(0, 0, cosmos.width, cosmos.height);
  const activeIds = drawRelations(ctx, now);

  const nodesToDraw = [];
  state.graph.forEachNode((id, attrs) => {
    const concept = conceptById(id);
    if (!concept || !conceptIsVisible(concept)) return;
    const display = nodeViewport(id);
    if (!display) return;
    nodesToDraw.push({ id, attrs, display, concept });
  });

  nodesToDraw.sort((a, b) => (a.display.size || 0) - (b.display.size || 0));
  nodesToDraw.forEach(({ id, attrs, display, concept }) => {
    if (!concept) return;
    const selected = state.selectedId === id;
    const hovered = state.hoveredId === id;
    const isProminent = state.prominentIds.includes(id);
    const accent = colourForConcept(concept);
    const seed = fieldSeed(id);
    const pulse = .92 + Math.sin(now * 0.0017 + seed) * 0.08;
    const baseSize = Math.max(attrs.visualSize || 3.2, 2.8) * pulse * .85;
    ctx.save();
    ctx.globalAlpha = activeIds.size && !activeIds.has(id) ? .32 : 1;

    const outerGlow = ctx.createRadialGradient(display.x, display.y, 0, display.x, display.y, baseSize * (selected ? 7.6 : hovered ? 6.2 : isProminent ? 4.8 : 3.9));
    outerGlow.addColorStop(0, rgba(accent, selected ? .24 : hovered ? .18 : isProminent ? .12 : .08));
    outerGlow.addColorStop(.34, rgba(accent, selected ? .14 : hovered ? .1 : isProminent ? .065 : .04));
    outerGlow.addColorStop(1, rgba(accent, 0));
    ctx.fillStyle = outerGlow;
    ctx.beginPath();
    ctx.arc(display.x, display.y, baseSize * (selected ? 7.6 : hovered ? 6.2 : isProminent ? 4.8 : 3.9), 0, Math.PI * 2);
    ctx.fill();

    const coreGlow = ctx.createRadialGradient(display.x, display.y, 0, display.x, display.y, baseSize * 2.7);
    coreGlow.addColorStop(0, 'rgba(255,255,255,.96)');
    coreGlow.addColorStop(.24, rgba(accent, .96));
    coreGlow.addColorStop(.7, rgba(accent, .54));
    coreGlow.addColorStop(1, rgba(accent, 0));
    ctx.fillStyle = coreGlow;
    ctx.beginPath();
    ctx.arc(display.x, display.y, baseSize * 2.7, 0, Math.PI * 2);
    ctx.fill();

    ctx.save();
    ctx.shadowBlur = selected ? 28 : hovered ? 22 : 14;
    ctx.shadowColor = rgba(accent, selected ? .55 : .34);
    ctx.fillStyle = '#f4f7ff';
    ctx.beginPath();
    ctx.arc(display.x, display.y, selected ? 2.6 : hovered ? 2.3 : 1.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    if (selected) {
      ctx.save();
      ctx.strokeStyle = rgba(accent, .28);
      ctx.lineWidth = 1.25;
      ctx.beginPath();
      ctx.arc(display.x, display.y, baseSize * 3.4, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
    ctx.restore();
  });

  const candidates = labelCandidates().sort((a, b) => b.priority - a.priority);
  const boxes = [];
  candidates.forEach(({ id, priority, concept }) => {
    const display = nodeViewport(id);
    if (!display) return;
    if (priority < 5 && (display.x < 20 || display.x > cosmos.width - 20 || display.y < 90 || display.y > cosmos.height - 50)) return;
    const selected = state.selectedId === id;
    const hovered = state.hoveredId === id;

    if (selected) {
      const accent = colourForConcept(concept);
      const descriptor = conceptLayerKey(concept).toUpperCase();
      const mobileSheet = window.matchMedia('(max-width: 720px)').matches;
      const panelRect = !$('#conceptPanel').hidden ? $('#conceptPanel').getBoundingClientRect() : null;
      const openWidth = panelRect && !mobileSheet ? panelRect.left - 14 : cosmos.width - 16;
      const bottom = panelRect && mobileSheet ? panelRect.top - 14 : cosmos.height - 50;
      const x = clamp(display.x + 26, 20, Math.max(20, openWidth - (mobileSheet ? 145 : 220)));
      const textWidth = Math.max(120, Math.min(mobileSheet ? 240 : 365, openWidth - x - 8));
      ctx.font = `${mobileSheet ? 600 : 580} ${mobileSheet ? 16 : 19}px "Inter", ui-sans-serif, sans-serif`;
      const lines = wrapFieldTitle(ctx, concept.title, textWidth);
      const blockHeight = 39 + lines.length * (mobileSheet ? 20 : 24);
      const y = clamp(display.y - 30, 110, Math.max(110, bottom - blockHeight));

      ctx.save();
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      ctx.shadowBlur = 14;
      ctx.shadowColor = 'rgba(0,0,0,.5)';
      ctx.font = '500 10px "Roboto Mono", ui-monospace, monospace';
      ctx.fillStyle = rgba(accent, .86);
      ctx.fillText(shortLabel(descriptor, Math.floor(textWidth / 6.2)), x, y);
      ctx.font = '500 10px "Roboto Mono", ui-monospace, monospace';
      ctx.fillStyle = '#9da8bb';
      ctx.fillText(concept.id, x, y + 21);
      ctx.font = `${mobileSheet ? 600 : 580} ${mobileSheet ? 16 : 19}px "Inter", ui-sans-serif, sans-serif`;
      ctx.fillStyle = '#f6f8ff';
      lines.forEach((line, index) => ctx.fillText(line, x, y + 47 + index * (mobileSheet ? 20 : 24)));
      ctx.restore();
      boxes.push({ x: x - 8, y: y - 12, width: textWidth + 16, height: blockHeight + 12 });
      return;
    }

    const title = shortLabel(concept.title, hovered ? 38 : 30);
    const fontSize = hovered ? 14 : 13;
    const fontWeight = hovered ? 600 : 560;
    ctx.font = `${fontWeight} ${fontSize}px "Inter", ui-sans-serif, sans-serif`;
    const textWidth = ctx.measureText(title).width + 20;
    const textHeight = fontSize + 14;
    const placement = placeLabelBox(cosmos.width, cosmos.height, display.x + (display.size || 6), display.y, textWidth, textHeight, display.x > cosmos.width * .62);
    const proposed = { x: placement.x, y: placement.y, width: textWidth, height: textHeight };
    const collides = boxes.some((box) => boxesIntersect(proposed, box));
    if (collides && priority < 5) return;

    const box = drawLabel(ctx, title, placement.x, placement.y, {
      fontSize,
      fontWeight,
      boxX: placement.x,
      boxY: placement.y,
      background: hovered ? 'rgba(10,13,19,.72)' : 'rgba(9,12,18,.52)',
      border: 'rgba(255,255,255,.08)',
      radius: 13,
      paddingX: 10,
      paddingY: 7
    });
    boxes.push(box);
  });
}

function drawClusterLabels(ctx) {
  if (state.selectedId || state.hoveredId) return;
  const {width,height}=state.cosmos;
  const boxes=[];
  ctx.save();
  ctx.font='500 10px "Roboto Mono", ui-monospace, monospace';
  ctx.textAlign='center';
  ctx.textBaseline='middle';
  for(const group of state.fieldGroups){
    if(hiddenLayersFor().has(group.key)||group.ids.length<2)continue;
    const points=group.ids.map(nodeViewport).filter(point=>point&&point.x>40&&point.x<width-60&&point.y>110&&point.y<height-70);
    if(points.length<2)continue;
    const x=median(points.map(p=>p.x));
    let y=Math.min(...points.map(p=>p.y))-26;
    const label=group.label.toUpperCase();
    const widthBox=ctx.measureText(label).width+22;
    let box={x:x-widthBox/2,y:y-10,width:widthBox,height:20};
    if(y<100||x-widthBox/2<20||x+widthBox/2>width-20)continue;
    for(let attempt=0;attempt<3&&boxes.some(b=>boxesIntersect(b,box));attempt++){
      y-=22;box.y=y-10;
    }
    if(y<100||boxes.some(b=>boxesIntersect(b,box)))continue;
    boxes.push(box);
    ctx.fillStyle=rgba(group.color,.58);
    ctx.shadowColor='rgba(0,0,0,.9)';ctx.shadowBlur=12;
    ctx.fillText(label,x,y);
  }
  ctx.restore();
}

function drawCosmos(now) {
  const cosmos=state.cosmos;
  if(!cosmos)return;
  cosmos.raf=null;
  if(!state.renderer||$('#fieldView').hidden||document.hidden)return;
  resizeCosmos(false);
  const moving=motionEnabled();
  if(moving)cosmos.time+=Math.min(.1,Math.max(0,(now-(cosmos.lastNow||now))/1000));
  cosmos.lastNow=now;
  const time=cosmos.time;
  const {width,height,starCtx,stars}=cosmos;
  const camera=state.renderer.getCamera().getState();
  if(moving){
    cosmos.pointer.x+=(cosmos.targetPointer.x-cosmos.pointer.x)*.045;
    cosmos.pointer.y+=(cosmos.targetPointer.y-cosmos.pointer.y)*.045;
  }
  if(cosmos.sceneDirty){
    const focus=state.selectedId||state.hoveredId;
    const activeIds=focus&&state.graph.hasNode(focus)?new Set([focus,...state.graph.neighbors(focus)]):null;
    const nodes=[];
    state.graph.forEachNode((id,attrs)=>{
      if(!nodeIsVisible(attrs))return;
      const display=state.renderer.getNodeDisplayData(id);
      if(display)nodes.push({id,x:display.x,y:display.y,color:colourForConcept(conceptById(id)),degree:attrs.degree||0});
    });
    cosmos.particles.setScene(nodes,state.fieldGroups,activeIds);
    cosmos.sceneDirty=false;
  }
  cosmos.particles.draw({renderer:state.renderer,time,pointer:cosmos.pointer});
  starCtx.clearRect(0,0,width,height);
  starCtx.globalCompositeOperation='source-over';
  stars.forEach((star,index)=>{
    const driftX=time*.45*star.drift-(camera.x-.5)*28*star.depth+cosmos.pointer.x*star.depth*5;
    const driftY=time*.2*star.drift+(camera.y-.5)*20*star.depth+cosmos.pointer.y*star.depth*5;
    const x=((star.x+driftX)%(width+40)+(width+40))%(width+40)-20;
    const y=((star.y+driftY)%(height+40)+(height+40))%(height+40)-20;
    const alpha=star.alpha*(.78+.22*Math.sin(time*.38*star.twinkle+star.phase));
    starCtx.fillStyle=index%5===0?`rgba(153,182,240,${alpha})`:`rgba(235,237,243,${alpha})`;
    starCtx.beginPath();starCtx.arc(x,y,star.size,0,Math.PI*2);starCtx.fill();
  });
  drawClusterLabels(starCtx);
  drawOverlay(time*1000);
  cosmos.lastDraw=now;
  if(moving)cosmos.raf=requestAnimationFrame(drawCosmos);
}


function median(values) {
  if (!values.length) return .5;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function defaultFieldState() {
  if (!state.renderer || !state.graph) return null;
  const points = state.graph.nodes()
    .filter((id) => nodeIsVisible(state.graph.getNodeAttributes(id)))
    .map((id) => state.renderer.getNodeDisplayData(id))
    .filter(Boolean);
  if (!points.length) return { x: .5, y: .5, ratio: .60 };

  const medianX = median(points.map((point) => point.x));
  const medianY = median(points.map((point) => point.y));
  const coreCount = Math.max(4, Math.ceil(points.length * .72));
  const core = [...points]
    .sort((a, b) => Math.hypot(a.x - medianX, a.y - medianY) - Math.hypot(b.x - medianX, b.y - medianY))
    .slice(0, coreCount);

  return {
    x: median(core.map((point) => point.x)),
    y: median(core.map((point) => point.y)),
    ratio: points.length > 80 ? .62 : points.length > 40 ? .56 : .53
  };
}

function applyDefaultFieldState() {
  if (!state.renderer) return;
  const target = defaultFieldState();
  if (!target) return;
  state.renderer.getCamera().setState(target);
  state.renderer.refresh();
}

function frameDefaultField(duration = 480) {
  if (!state.renderer) return;
  const target = defaultFieldState();
  if (!target) return;
  if (motionEnabled()) state.renderer.getCamera().animate(target, { duration });
  else state.renderer.getCamera().setState(target);
}

function frameSelectedConcept(id, { keepZoom = false } = {}) {
  if (!state.renderer || !state.graph?.hasNode(id)) return;
  const display = state.renderer.getNodeDisplayData(id);
  if (!display) return;
  const camera = state.renderer.getCamera();
  const current = camera.getState();
  const fieldWidth = state.cosmos?.width || window.innerWidth;
  const fieldHeight = state.cosmos?.height || window.innerHeight;
  const panel = $('#conceptPanel');
  const mobileSheet = window.matchMedia('(max-width: 720px)').matches;
  const panelRect = panel && !panel.hidden ? { top: panel.offsetTop, width: panel.offsetWidth } : null;
  if (panelRect) $('#fieldView').style.setProperty('--panel-reserve', `${panelRect.width + 38}px`);

  let desiredViewport;
  if (mobileSheet && panelRect) {
    const visibleFieldTop = 64;
    const visibleFieldBottom = Math.max(150, panelRect.top - 14);
    const visibleFieldHeight = Math.max(120, visibleFieldBottom - visibleFieldTop);
    desiredViewport = {
      x: fieldWidth * .36,
      y: Math.max(118, Math.min(visibleFieldTop + (visibleFieldHeight * .60), visibleFieldBottom - 42))
    };
  } else {
    const panelWidth = panelRect ? panelRect.width + 54 : 0;
    const openWidth = Math.max(420, fieldWidth - panelWidth);
    desiredViewport = {
      x: Math.max(230, Math.min(openWidth * .52, openWidth - 190)),
      y: Math.max(170, Math.min(fieldHeight * .50, fieldHeight - 150))
    };
  }

  const targetRatio = keepZoom ? current.ratio : Math.max(.08, Math.min(.30, current.ratio * .58));

  if (typeof state.renderer.viewportToFramedGraph === 'function') {
    const desiredFramed = state.renderer.viewportToFramedGraph(desiredViewport, { cameraState: { ...current, ratio: targetRatio } });
    const target = {
      x: current.x + (display.x - desiredFramed.x),
      y: current.y + (display.y - desiredFramed.y),
      ratio: targetRatio
    };
    if (keepZoom || !motionEnabled()) camera.setState(target);
    else camera.animate(target, { duration: 560 });
    return;
  }

  const target = { x: display.x, y: display.y, ratio: targetRatio };
  if (motionEnabled()) camera.animate(target, { duration: 560 });
  else camera.setState(target);
}

async function openConcept(id, updateHash = false) {
  const concept = conceptById(id) || await api(`/api/concepts/${encodeURIComponent(id)}`);
  if (!state.conceptIndex.has(concept.id)) state.conceptIndex.set(concept.id, concept);
  if (!conceptIsVisible(concept)) {
    hiddenLayersFor().delete(conceptLayerKey(concept));
    saveHiddenLayers();
    renderLegend();
  }
  state.selectedId = concept.id;
  $('#fieldView').classList.add('has-selection');
  state.renderer?.refresh();
  invalidateCosmos(true);
  $('#panelId').textContent = concept.id;
  $('#panelField').textContent = concept.field;
  $('#panelTitle').textContent = concept.title;
  $('#panelSummary').textContent = concept.summary;
  $('#panelBody').textContent = concept.body;
  $('#panelDate').textContent = formatDate(concept.createdAt);
  $('#panelLicence').textContent = concept.licence;
  $('#panelContributor').textContent = concept.contributor;
  $('#panelKind').textContent = concept.kind;
  $('#panelTags').innerHTML = (concept.tags || []).map((tag) => `<span class="tag">${escapeHtml(tag)}</span>`).join('');
  $('#panelHash').textContent = concept.hash;
  $('#panelAnalysis').textContent = concept.analysisSource === 'openai' ? 'OpenAI metadata' : 'Registry metadata';

  const related = concept.related || [];
  $('#relationSection').hidden = related.length === 0;
  $('#relatedList').innerHTML = related.map((item) => `
    <button class="related-item" data-concept="${escapeHtml(item.id)}">
      <span>${escapeHtml(item.id)}</span><strong>${escapeHtml(item.title)}</strong><em>${Math.round(item.score * 100)}%</em>
    </button>`).join('');

  $$('#relatedList [data-concept]').forEach((button) => button.addEventListener('click', () => openConcept(button.dataset.concept, true)));

  const contact = $('#contactLink');
  if (concept.contactPublic && concept.contact) {
    contact.hidden = false;
    contact.href = /^https?:\/\//i.test(concept.contact) ? concept.contact : `mailto:${concept.contact}`;
    contact.target = /^https?:\/\//i.test(concept.contact) ? '_blank' : '_self';
  } else contact.hidden = true;

  const panel = $('#conceptPanel');
  panel.hidden = false;
  panel.classList.remove('is-expanded');
  $('#panelGrabber')?.setAttribute('aria-expanded', 'false');
  panel.querySelector('.panel-scroll').scrollTop = 0;

  if (state.graph?.hasNode(concept.id)) {
    requestAnimationFrame(() => frameSelectedConcept(concept.id));
  }
  if (updateHash) history.replaceState(null, '', `#${concept.id}`);
}

function closeConcept(updateHash = true) {
  $('#conceptPanel').hidden = true;
  $('#conceptPanel').classList.remove('is-expanded');
  $('#panelGrabber')?.setAttribute('aria-expanded', 'false');
  state.selectedId = null;
  $('#fieldView').classList.remove('has-selection');
  if (!$('#fieldView').hidden) state.renderer?.refresh();
  invalidateCosmos(true);
  if (updateHash && location.hash) history.replaceState(null, '', location.pathname);
}

function routeFromHash() {
  const id = location.hash.replace(/^#/, '');
  if (/^C-\d{4}-\d{6}$/i.test(id)) openConcept(id, false).catch(() => {});
}

function resetCamera() {
  frameDefaultField(520);
}

function openSearchModal() {
  $('#searchModal').hidden = false;
  setTimeout(() => $('#searchInput').focus(), 30);
}

function closeSearchModal() {
  $('#searchModal').hidden = true;
  $('#searchResults').hidden = true;
}

async function searchConcepts(query) {
  const resultsBox = $('#searchResults');
  if (!query.trim()) { resultsBox.hidden = true; return; }
  const results = await api(`/api/concepts?q=${encodeURIComponent(query.trim())}`);
  resultsBox.innerHTML = results.length
    ? results.slice(0, 12).map((concept) => `<button class="search-result" data-id="${concept.id}"><span>${concept.id}</span><strong>${escapeHtml(concept.title)}</strong></button>`).join('')
    : `<div class="search-empty">No concepts found for “${escapeHtml(query)}”.</div>`;
  resultsBox.hidden = false;
  $$('.search-result').forEach((button) => button.addEventListener('click', () => {
    $('#searchInput').value = '';
    closeSearchModal();
    setView('field');
    openConcept(button.dataset.id, true);
  }));
}

function openAdd(buildsOn = '') {
  $('#addModal').hidden = false;
  $('#buildsOn').value = buildsOn;
  if (buildsOn) {
    const source = conceptById(buildsOn);
    $('#derivedBanner').hidden = false;
    $('#derivedBanner').textContent = `This contribution will record that it builds on ${buildsOn}${source ? ` — ${source.title}` : ''}.`;
  } else {
    $('#derivedBanner').hidden = true;
    $('#derivedBanner').textContent = '';
  }
  setTimeout(() => $('#conceptTitle').focus(), 30);
}

function closeAdd() {
  $('#addModal').hidden = true;
}

async function submitConcept(event) {
  event.preventDefault();
  const button = $('#registerButton');
  const error = $('#formError');
  error.hidden = true;
  button.disabled = true;
  button.innerHTML = 'Registering…';
  try {
    const payload = {
      title: $('#conceptTitle').value,
      body: $('#conceptBody').value,
      kind: $('#conceptKind').value,
      licence: $('#conceptLicence').value,
      contributor: $('#conceptContributor').value,
      contact: $('#conceptContact').value,
      contactPublic: $('#contactPublic').checked,
      collaborationOpen: $('#collaborationOpen').checked,
      buildsOn: $('#buildsOn').value ? [$('#buildsOn').value] : []
    };
    const concept = await api('/api/concepts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    $('#conceptForm').reset();
    $('#collaborationOpen').checked = true;
    $('#bodyCount').textContent = '0';
    closeAdd();
    showToast(`${concept.id} registered.`);
    const [concepts, graphData, status] = await Promise.all([api('/api/concepts'), api('/api/graph'), api('/api/status')]);
    state.concepts = concepts;
    state.conceptIndex = new Map(concepts.map(concept => [concept.id, concept]));
    state.graphData = graphData;
    state.fieldGroups = buildLayerGroups(graphData.nodes);
    $('#registryStatus').textContent = `${status.concepts} concepts · open registry`;
    $('#fieldCount').textContent = `${status.concepts} registered concepts · ${graphData.edges.length} conceptual resonances`;
    renderLegend();
    renderGraph();
    openConcept(concept.id, true);
  } catch (err) {
    error.textContent = err.message;
    error.hidden = false;
  } finally {
    button.disabled = false;
    button.innerHTML = 'Register concept <span>↗</span>';
  }
}

async function renderLedger(reload = false) {
  if (reload || !state.ledgerEvents.length) state.ledgerEvents = await api('/api/ledger?limit=500');

  const enriched = state.ledgerEvents.map((event) => {
    const concept = conceptById(event.conceptId);
    return {
      ...event,
      concept,
      author: concept?.contributor || 'Anonymous',
      licence: event.detail?.licence || concept?.licence || '—',
      title: concept?.title || event.conceptId
    };
  });

  const rows = enriched;

  const direction = state.ledgerDirection === 'asc' ? 1 : -1;
  rows.sort((a, b) => {
    let left;
    let right;
    if (state.ledgerSort === 'concept') { left = a.title; right = b.title; }
    else if (state.ledgerSort === 'author') { left = a.author; right = b.author; }
    else if (state.ledgerSort === 'licence') { left = a.licence; right = b.licence; }
    else { left = new Date(a.at).getTime(); right = new Date(b.at).getTime(); }
    if (typeof left === 'number') return (left - right) * direction;
    return String(left).localeCompare(String(right)) * direction;
  });

  const totalPages = Math.max(1, Math.ceil(rows.length / state.ledgerPageSize));
  state.ledgerPage = Math.min(Math.max(1, state.ledgerPage), totalPages);
  const start = (state.ledgerPage - 1) * state.ledgerPageSize;
  const pageRows = rows.slice(start, start + state.ledgerPageSize);

  $('#ledgerBody').innerHTML = pageRows.map((row) => `
    <tr>
      <td><span class="ledger-date">${escapeHtml(formatDate(row.at, true))}</span></td>
      <td><button class="ledger-concept ledger-concept-rich" data-ledger-concept="${escapeHtml(row.conceptId)}"><strong>${escapeHtml(row.title)}</strong><span>${escapeHtml(row.conceptId)}</span></button></td>
      <td>${escapeHtml(row.author)}</td>
      <td>${escapeHtml(row.licence)}</td>
    </tr>`).join('');

  $$('[data-ledger-concept]').forEach((button) => button.addEventListener('click', () => {
    setView('field');
    openConcept(button.dataset.ledgerConcept, true);
  }));

  const first = rows.length ? start + 1 : 0;
  const last = Math.min(start + state.ledgerPageSize, rows.length);
  $('#ledgerPageInfo').textContent = `${first}–${last} of ${rows.length}`;
  $('#ledgerPrev').disabled = state.ledgerPage <= 1;
  $('#ledgerNext').disabled = state.ledgerPage >= totalPages;

  $$('[data-ledger-sort]').forEach((button) => {
    const active = button.dataset.ledgerSort === state.ledgerSort;
    button.classList.toggle('is-active', active);
    const indicator = button.querySelector('span');
    if (indicator) indicator.textContent = active ? (state.ledgerDirection === 'asc' ? '↑' : '↓') : '↕';
  });
}

function setView(view) {
  if (view === 'about') {
    $('#aboutModal').hidden = false;
    return;
  }
  state.currentView = view;
  $('#aboutModal').hidden = true;
  $('#fieldView').hidden = view !== 'field';
  $('#ledgerView').hidden = view !== 'ledger';
  $$('.nav-action').forEach((button) => button.classList.toggle('is-active', button.dataset.view === view));
  if (view === 'ledger') {
    stopCosmos();
    closeConcept();
    renderLedger(!state.ledgerEvents.length).catch((err) => showToast(err.message, 'error'));
  }
  if (view === 'field') setTimeout(() => { state.renderer?.refresh(); startCosmos(); }, 0);
}

function updateMotionButton() {
  const button = $('#motionToggle');
  const paused = !motionEnabled();
  button.classList.toggle('is-paused', paused);
  button.setAttribute('aria-pressed', String(paused));
  button.setAttribute('aria-label', paused ? 'Resume field motion' : 'Pause field motion');
  button.title = paused ? 'Resume motion' : 'Pause motion';
}

function wireUI() {
  $('#addButton').addEventListener('click', () => openAdd());
  $('#openSearch').addEventListener('click', openSearchModal);
  $('#closePanel').addEventListener('click', () => closeConcept());
  $('#panelGrabber')?.addEventListener('click', () => {
    const panel = $('#conceptPanel');
    const expanded = panel.classList.toggle('is-expanded');
    $('#panelGrabber').setAttribute('aria-expanded', String(expanded));
    if (!expanded && state.selectedId) setTimeout(() => frameSelectedConcept(state.selectedId, { keepZoom: true }), motionEnabled() ? 260 : 0);
  });
  $('#homeButton').addEventListener('click', () => { setView('field'); closeConcept(); resetCamera(); });
  $('#zoomIn').addEventListener('click', () => state.renderer?.getCamera().animatedZoom({ duration: 250, factor: 1.45 }));
  $('#zoomOut').addEventListener('click', () => state.renderer?.getCamera().animatedUnzoom({ duration: 250, factor: 1.45 }));
  $('#centreGraph').addEventListener('click', resetCamera);
  const discovered = new Set();
  $('#discoverConcept').addEventListener('click', () => {
    let candidates = state.concepts.filter(concept => conceptIsVisible(concept) && concept.id !== state.selectedId && !discovered.has(concept.id));
    if (!candidates.length) { discovered.clear(); candidates = state.concepts.filter(concept => conceptIsVisible(concept)); }
    if (!candidates.length) { showToast('Show a layer to explore its concepts.'); return; }
    const concept = candidates[Math.floor(Math.random() * candidates.length)];
    discovered.add(concept.id);
    openConcept(concept.id, true).catch(error => showToast(error.message, 'error'));
  });
  $('#motionToggle').addEventListener('click', () => {
    state.motionMode = motionEnabled() ? 'off' : 'on';
    localStorage.setItem('conceptual:motion', state.motionMode);
    updateMotionButton();
    stopCosmos();
    startCosmos();
  });
  updateMotionButton();
  window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', event => {
    state.reducedMotion = event.matches;
    updateMotionButton();
    stopCosmos();
    startCosmos();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stopCosmos(); else startCosmos();
  });
  $('#legendToggle').addEventListener('click', () => { $('#legend').hidden = !$('#legend').hidden; });
  document.addEventListener('pointerdown', (event) => {
    const legend = $('#legend');
    const toggle = $('#legendToggle');
    if (!legend || legend.hidden) return;
    if (legend.contains(event.target) || toggle?.contains(event.target)) return;
    legend.hidden = true;
  });
  $('#copyRef').addEventListener('click', async () => {
    const concept = conceptById(state.selectedId);
    if (!concept) return;
    await navigator.clipboard.writeText(`${concept.id} — ${concept.title} · ${location.origin}/#${concept.id}`);
    showToast('Reference copied.');
  });
  $('#buildOn').addEventListener('click', () => state.selectedId && openAdd(state.selectedId));
  $$('.nav-action').forEach((button) => button.addEventListener('click', () => setView(button.dataset.view)));
  $$('[data-ledger-sort]').forEach((button) => button.addEventListener('click', () => {
    const key = button.dataset.ledgerSort;
    if (state.ledgerSort === key) state.ledgerDirection = state.ledgerDirection === 'asc' ? 'desc' : 'asc';
    else {
      state.ledgerSort = key;
      state.ledgerDirection = key === 'at' ? 'desc' : 'asc';
    }
    state.ledgerPage = 1;
    renderLedger().catch((err) => showToast(err.message, 'error'));
  }));
  $('#ledgerPageSize').addEventListener('change', (event) => {
    state.ledgerPageSize = Number(event.target.value) || 20;
    state.ledgerPage = 1;
    renderLedger().catch((err) => showToast(err.message, 'error'));
  });
  $('#ledgerPrev').addEventListener('click', () => {
    if (state.ledgerPage > 1) state.ledgerPage -= 1;
    renderLedger().catch((err) => showToast(err.message, 'error'));
  });
  $('#ledgerNext').addEventListener('click', () => {
    state.ledgerPage += 1;
    renderLedger().catch((err) => showToast(err.message, 'error'));
  });
  $$('[data-close="add"]').forEach((el) => el.addEventListener('click', closeAdd));
  $$('[data-close="about"]').forEach((el) => el.addEventListener('click', () => { $('#aboutModal').hidden = true; }));
  $$('[data-close="search"]').forEach((el) => el.addEventListener('click', closeSearchModal));
  $('#conceptForm').addEventListener('submit', submitConcept);
  $('#conceptBody').addEventListener('input', (event) => { $('#bodyCount').textContent = event.target.value.length; });
  $('#conceptLicence').addEventListener('change', (event) => {
    $('#licenceExplainer').innerHTML = event.target.value === 'CC BY 4.0'
      ? '<strong>CC BY:</strong> others may reuse the submitted material, including commercially, provided attribution is given under the licence terms.'
      : '<strong>CC0:</strong> you are intentionally offering this submitted material for unrestricted reuse as far as the licence permits.';
  });

  let searchTimer;
  $('#searchInput').addEventListener('input', (event) => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => searchConcepts(event.target.value).catch((err) => showToast(err.message, 'error')), 130);
  });
  $('#searchInput').addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeSearchModal();
      event.target.blur();
    }
    if (event.key === 'Enter') {
      const first = $('.search-result');
      if (first) { event.preventDefault(); first.click(); }
    }
  });

  document.addEventListener('keydown', (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      openSearchModal();
    }
    if (event.key === 'Escape') {
      if (!$('#searchModal').hidden) closeSearchModal();
      else if (!$('#addModal').hidden) closeAdd();
      else if (!$('#aboutModal').hidden) $('#aboutModal').hidden = true;
      else if (!$('#conceptPanel').hidden) closeConcept();
    }
  });

  window.addEventListener('hashchange', routeFromHash);
  window.addEventListener('beforeunload', stopCosmos);
}

wireUI();
loadApp().catch((error) => {
  console.error(error);
  showToast('The conceptual field could not be loaded.', 'error');
  $('#fieldCount').textContent = 'Registry unavailable';
});
